import { Tag } from "antd";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  buildSearchQuery,
  numberValue,
  QueryFieldMapping,
} from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";

export interface RewardsViewForm {
  readonly name: string;
  readonly description: string;
  readonly requiredPointsMin: number;
  readonly requiredPointsMax: number;
  readonly isPublished: boolean;
  readonly isPlatformWide: boolean;
}

export const rewardsViewColumns: DataColumn[] = [
  {
    title: "Name",
    key: "name",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    // Platform-owned rewards have no company; surface that explicitly.
    title: "Owner",
    key: "companyName",
    width: 168,
    sort: true,
    transform: (value: string) =>
      value ? value : <Tag color="blue">Platform</Tag>,
  },
  {
    title: "Required points",
    key: "requiredPoints",
    width: 140,
    sort: true,
    type: "badge",
  },
  {
    title: "Published",
    key: "isPublished",
    width: 120,
    sort: true,
    transform: (value: boolean) => (
      <Tag color={value ? "green" : "default"}>
        {value ? "Published" : "Draft"}
      </Tag>
    ),
  },
  {
    title: "Platform-wide",
    key: "isPlatformWide",
    width: 140,
    sort: true,
    transform: (value: boolean) =>
      value ? (
        <Tag color="geekblue">Platform-wide</Tag>
      ) : (
        <Tag color="default">Targeted</Tag>
      ),
  },
  {
    title: "Users",
    key: "rewardedUsersCount",
    width: 100,
    sort: true,
    type: "badge",
  },
  {
    title: "Departments",
    key: "rewardedDepartmentsCount",
    width: 120,
    sort: true,
    type: "badge",
  },
  {
    title: "Companies",
    key: "rewardedCompaniesCount",
    width: 120,
    sort: true,
    type: "badge",
  },
  {
    title: "Description",
    key: "description",
    width: 240,
    sort: true,
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
];

const rewardsQueryMappings: QueryFieldMapping[] = [
  { formField: "name", operation: QueryOperation.Contains },
  { formField: "description", operation: QueryOperation.Contains },
  {
    formField: "requiredPointsMin",
    queryField: "requiredPoints",
    operation: QueryOperation.GreaterThanOrEqual,
    transform: numberValue,
  },
  {
    formField: "requiredPointsMax",
    queryField: "requiredPoints",
    operation: QueryOperation.LessThanOrEqual,
    transform: numberValue,
  },
  { formField: "isPublished", operation: QueryOperation.Equal },
  { formField: "isPlatformWide", operation: QueryOperation.Equal },
];

export const mapRewardsViewQuery = (values: RewardsViewForm): SearchQuery =>
  buildSearchQuery(values, rewardsQueryMappings);

export const rewardsSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        { type: "textbox", name: "name", label: "Name" },
        { type: "textbox", name: "description", label: "Description" },
        { type: "numberbox", name: "requiredPointsMin", label: "Min points" },
        { type: "numberbox", name: "requiredPointsMax", label: "Max points" },
        { type: "togglebox", name: "isPublished", label: "Published" },
        { type: "togglebox", name: "isPlatformWide", label: "Platform-wide" },
      ],
    },
  ],
};
